import { useEffect, useState } from "react";
import axios from "axios";
import DashboardLayout, { LayoutProps } from "../components/DashboardLayout";
import { dispositivoPollingService } from "../services/dispositivoPollingService";

interface DispositivoStatus {
  configured: boolean;
  device_ip: string | null;
  device_login: string;
  webhook_url: string | null;
  webhook_token_configured: boolean;
  last_sync_at: string | null;
  last_event_id: string;
  control_id_pontos_count: number;
}

interface Props extends LayoutProps {
  statusPath: string;
  syncPath: string;
  resetPath: string;
  configureWebhookPath: string;
}

export default function DispositivoPage({
  statusPath,
  syncPath,
  resetPath,
  configureWebhookPath,
  csrfToken,
  flash,
}: Props) {
  const [status, setStatus] = useState<DispositivoStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{
    msg: string;
    type: "success" | "error";
  } | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // which button is loading

  // Polling automático - persiste no localStorage
  const [autoPolling, setAutoPolling] = useState(() => {
    const saved = localStorage.getItem("dispositivo_auto_polling");
    return saved === "true";
  });

  const [pollingInterval, setPollingInterval] = useState(() => {
    const saved = localStorage.getItem("dispositivo_polling_interval");
    return saved ? parseInt(saved) : 30;
  });

  const headers = {
    "Content-Type": "application/json",
    "X-CSRF-Token": csrfToken,
    Accept: "application/json",
  };

  function showToast(msg: string, type: "success" | "error") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  }

  async function loadStatus() {
    try {
      const res = await axios.get<DispositivoStatus>(statusPath, {
        headers: { Accept: "application/json" },
      });
      setStatus(res.data);
    } catch {
      showToast("Erro ao carregar status do dispositivo.", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadStatus();
  }, []);

  // Persiste configurações de polling no localStorage
  useEffect(() => {
    localStorage.setItem("dispositivo_auto_polling", String(autoPolling));
    // Reinicia o serviço global ao mudar configuração
    dispositivoPollingService.restart();
  }, [autoPolling]);

  useEffect(() => {
    localStorage.setItem(
      "dispositivo_polling_interval",
      String(pollingInterval),
    );
    // Reinicia o serviço global ao mudar intervalo
    dispositivoPollingService.restart();
  }, [pollingInterval]);

  // Recarrega status periodicamente quando na página (apenas visualização)
  useEffect(() => {
    if (!autoPolling) return;

    const statusInterval = setInterval(() => {
      loadStatus();
    }, pollingInterval * 1000);

    return () => clearInterval(statusInterval);
  }, [autoPolling, pollingInterval]);

  async function postAction(path: string, key: string, confirmMsg?: string) {
    if (confirmMsg && !confirm(confirmMsg)) return;
    setBusy(key);
    try {
      const res = await axios.post<{ notice?: string; alert?: string }>(
        path,
        {},
        { headers },
      );
      const msg = res.data.notice ?? res.data.alert ?? "Concluído.";
      const type = res.data.alert ? "error" : "success";
      showToast(msg, type);
      await loadStatus();
    } catch (err: any) {
      const msg = err?.response?.data?.alert ?? "Erro ao executar ação.";
      showToast(msg, "error");
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <DashboardLayout flash={flash}>
        <div className="flex items-center justify-center py-24 text-gray-400 gap-3">
          <svg
            className="animate-spin w-5 h-5 text-blue-500"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8H4z"
            />
          </svg>
          <span className="text-sm">Carregando...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout flash={flash}>
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Toast */}
        {toast && (
          <div
            className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-lg shadow-lg text-sm text-white
          ${toast.type === "success" ? "bg-green-600" : "bg-red-600"}`}
          >
            {toast.msg}
          </div>
        )}

        {/* Cabeçalho */}
        <div className="flex items-center gap-3">
          <div className="bg-blue-100 rounded-xl p-2.5">
            <svg
              className="w-7 h-7 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18"
              />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Integração com Aparelho iDClass
            </h1>
            <p className="text-gray-500 text-sm">Control iD — Bio Prox</p>
          </div>
        </div>

        {/* Configuração */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Configuração
            </h2>
          </div>
          <div className="px-5 py-4 space-y-3 text-sm">
            {status?.configured ? (
              <>
                <div className="flex items-center gap-2 text-green-700">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">Aparelho configurado</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-gray-600">
                  <span className="font-medium text-gray-500">IP:</span>
                  <span className="font-mono">{status.device_ip}</span>
                  <span className="font-medium text-gray-500">Login:</span>
                  <span>{status.device_login}</span>
                  <span className="font-medium text-gray-500">Senha:</span>
                  <span>••••••••</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 text-amber-700">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">Aparelho não configurado</span>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800 text-xs space-y-1">
                  <p className="font-semibold">
                    Adicione ao arquivo{" "}
                    <code className="bg-amber-100 px-1 rounded">.env</code>:
                  </p>
                  <pre className="font-mono mt-1">{`CONTROL_ID_IP=192.168.1.100\nCONTROL_ID_LOGIN=admin\nCONTROL_ID_PASSWORD=sua_senha`}</pre>
                  <p className="text-amber-700 mt-1">
                    Reinicie o servidor após salvar o{" "}
                    <code className="bg-amber-100 px-1 rounded">.env</code>.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Status da Sincronização */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Status da Sincronização
            </h2>
          </div>
          <div className="px-5 py-4 space-y-2 text-sm text-gray-600">
            <div className="flex justify-between">
              <span className="text-gray-500">Última atualização:</span>
              <span className="font-medium">
                {status?.last_sync_at ? (
                  new Date(status.last_sync_at).toLocaleString("pt-BR")
                ) : (
                  <span className="text-gray-400">Nenhuma ainda</span>
                )}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">
                Último evento processado (ID):
              </span>
              <span className="font-mono font-medium">
                {status?.last_event_id ?? "0"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Pontos no banco (iDClass):</span>
              <span className="font-medium">
                {status?.control_id_pontos_count ?? 0}
              </span>
            </div>
          </div>
        </div>

        {/* Polling Automático */}
        {/* <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Sincronização Automática (Polling)
            </h2>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={autoPolling}
                onChange={(e) => setAutoPolling(e.target.checked)}
                disabled={!status?.configured}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:bg-gray-300 peer-disabled:cursor-not-allowed"></div>
            </label>
          </div>
          <div className="px-5 py-4 space-y-4 text-sm">
            <p className="text-gray-600">
              {autoPolling
                ? "🔄 Sincronização automática ativada - buscando novos registros periodicamente"
                : "Ative para buscar automaticamente novos registros do aparelho em intervalos regulares"}
            </p>
            <div className="flex items-center gap-3">
              <label className="text-gray-500 text-sm">
                Intervalo (segundos):
              </label>
              <input
                type="number"
                min="10"
                max="300"
                step="10"
                value={pollingInterval}
                onChange={(e) =>
                  setPollingInterval(parseInt(e.target.value) || 30)
                }
                disabled={!status?.configured}
                className="w-20 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <span className="text-gray-400 text-xs">
                (recomendado: 30-60s)
              </span>
            </div>
            {autoPolling && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-800 text-xs">
                ⏱️ Próxima sincronização em ~{pollingInterval} segundos
              </div>
            )}
          </div>
        </div> */}

        {/* Webhook */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Modo Push — Webhook
            </h2>
            <span className="text-xs bg-blue-100 text-blue-700 font-semibold px-2 py-0.5 rounded-full">
              Recomendado
            </span>
          </div>
          <div className="px-5 py-4 space-y-4 text-sm">
            <p className="text-gray-600">
              O aparelho enviará um POST para esta URL a cada batida de ponto:
            </p>
            {status?.webhook_url && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 font-mono text-xs text-gray-800 break-all select-all">
                {status.webhook_url}
              </div>
            )}
            {!status?.webhook_token_configured && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 text-yellow-800 text-xs">
                Sem token configurado — qualquer requisição é aceita. Defina{" "}
                <code className="bg-yellow-100 px-1 rounded">
                  CONTROL_ID_WEBHOOK_TOKEN
                </code>{" "}
                no <code>.env</code> para proteger o endpoint.
              </div>
            )}
            <button
              onClick={() =>
                postAction(
                  configureWebhookPath,
                  "webhook",
                  "Isso enviará a URL do webhook para o aparelho via API. Continuar?",
                )
              }
              disabled={!status?.configured || busy === "webhook"}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition
              ${status?.configured ? "bg-blue-600 hover:bg-blue-700 cursor-pointer" : "bg-gray-400 cursor-not-allowed"}`}
            >
              {busy === "webhook"
                ? "Configurando..."
                : "Configurar URL no aparelho automaticamente"}
            </button>

            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer hover:text-gray-700 select-none">
                Configurar manualmente no aparelho
              </summary>
              <div className="mt-2 space-y-1 text-gray-600">
                <p>
                  Na interface web do aparelho →{" "}
                  <strong>
                    Configurações → Rede → Servidor de Monitoramento
                  </strong>
                  , cole a URL acima.
                </p>
              </div>
            </details>
          </div>
        </div>

        {/* Ações manuais */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => postAction(syncPath, "sync")}
            disabled={!status?.configured || busy === "sync"}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition
            ${status?.configured && busy !== "sync" ? "bg-green-600 text-white hover:bg-green-700 cursor-pointer" : "bg-gray-400 text-gray-200 cursor-not-allowed"}`}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
              />
            </svg>
            {busy === "sync" ? "Importando..." : "Importar dados do aparelho"}
          </button>
          <button
            onClick={() =>
              postAction(
                resetPath,
                "reset",
                "O próximo sync reimportará todos os eventos. Continuar?",
              )
            }
            disabled={busy === "reset"}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-700
                     bg-white border border-gray-300 hover:bg-gray-50 transition cursor-pointer"
          >
            {busy === "reset"
              ? "Reiniciando..."
              : "Reiniciar contador (reimportar tudo)"}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}
